import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { requirementApi, transferApi, procurementApi, materialUnitApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from '../../components/common/StatusBadge';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const priorityLabel = { 1: '🔴 High', 2: '🟡 Medium', 3: '🟢 Low' };

const getReqStatusLabel = (r) => {
  const procs       = r.procurements || [];
  const qtyReceived = procs.reduce((sum, p) => (p.status === 'received' ? sum + p.quantity : sum), 0);
  const inTransit   = procs.filter((p) => p.status === 'sent').length;

  switch (r.status) {
    case 'open':      return 'Open';
    case 'cancelled': return 'Cancelled';
    case 'received':  return 'Fully Received';
    case 'partially_fulfilled':
      if (qtyReceived === 0) return 'Partially Fulfilled';
      if (inTransit > 0)     return 'Partially Received';
      return 'Partially Received (Pending Dispatch)';
    case 'fulfilled':
      return qtyReceived === 0 ? 'Fully Dispatched' : 'In Transit';
    default: return r.status?.replace(/_/g, ' ');
  }
};

export default function RequirementDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission, isSiteRestricted } = useAuth();

  const canWrite   = hasPermission('requirement_creation');
  const canSend    = hasPermission('transfer_approval');
  const canReceive = hasPermission('transfer_request') || hasPermission('requirement_creation');

  const [req, setReq]                         = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [refreshing, setRefreshing]           = useState(false);
  const [transferForm, setTransferForm]       = useState(null);
  // Only populated when req.kit.requiresMaterialId — the source site's
  // currently IN_STOCK units, so the transfer can name exactly which
  // physical units are moving (mirrors the picker used for Issue/Damage
  // Flagging elsewhere in the app).
  const [transferAvailableUnits, setTransferAvailableUnits] = useState([]);
  const [transferUnitIds, setTransferUnitIds] = useState([]);
  const [editForm, setEditForm]               = useState(null);
  const [procurementForm, setProcurementForm] = useState(false);
  const [procForm, setProcForm]               = useState({ quantity: '', remarks: '' });
  const [procSentConfirm, setProcSentConfirm] = useState(false);          // confirm dialog after form fill
  const [receiveConfirm, setReceiveConfirm]   = useState(null);           // null | { step:1|2, procId, proc }
  const [deleteConfirm, setDeleteConfirm]     = useState(false);
  const [cancelProcId, setCancelProcId]       = useState(null);
  const [error, setError]                     = useState('');

  const load = async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await requirementApi.getOne(id);
      setReq(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load requirement');
    } finally {
      setLoading(false);
    }
  };

  // Initial load + auto-refresh every 15 s so status updates from other users appear live
  useEffect(() => {
    load(true);                                    // show spinner on first load
    const interval = setInterval(() => load(), 15_000);  // silent background ticks
    return () => clearInterval(interval);
  }, [id]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError('');
    try {
      await requirementApi.refreshSuggestions(id);
      await new Promise((r) => setTimeout(r, 2000));
      await load();
    } catch (err) {
      // Previously unhandled — a failed refresh (e.g. a permission 403) left
      // the button stuck on "Refreshing..." forever with no visible error.
      setError(err.response?.data?.message || 'Failed to refresh suggestions');
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch the source site's IN_STOCK units whenever the Transfer modal opens
  // (or the quantity changes) for a requiresMaterialId kit — mirrors the
  // pattern used for Issue/Damage Flagging pickers elsewhere in the app.
  useEffect(() => {
    if (!transferForm || !req?.kit?.requiresMaterialId) { setTransferAvailableUnits([]); return; }
    setTransferUnitIds([]);
    materialUnitApi.list({ kitId: req.kitId, siteId: transferForm.fromSiteId, status: 'IN_STOCK' })
      .then((res) => setTransferAvailableUnits(res.data.data || []))
      .catch(() => setTransferAvailableUnits([]));
  }, [transferForm?.fromSiteId, req?.kitId, req?.kit?.requiresMaterialId]);

  const toggleTransferUnit = (id) => {
    setTransferUnitIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await transferApi.create({
        fromSiteId:    transferForm.fromSiteId,
        toSiteId:      req.siteId,
        kitId:         req.kitId,
        quantity:      parseInt(transferForm.quantity),
        requirementId: req.id,
        remarks:       transferForm.remarks || null,
        materialUnitIds: req.kit?.requiresMaterialId ? transferUnitIds : undefined,
      });
      setTransferForm(null);
      // Stay on this page (not navigate to /transfers) so the suggestion's
      // button immediately reflects the new pending transfer as "Cancel".
      load();
    } catch (err) { setError(err.response?.data?.message || 'Error creating transfer'); }
  };

  const handleCancelTransfer = async (transferId) => {
    setError('');
    try {
      await transferApi.cancel(transferId);
      load();
    } catch (err) { setError(err.response?.data?.message || 'Error cancelling transfer'); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await requirementApi.update(id, {
        quantityRequired: parseInt(editForm.quantityRequired),
        expectedDate:     editForm.expectedDate || null,
        priority:         parseInt(editForm.priority),
        remarks:          editForm.remarks || null,
      });
      setEditForm(null);
      load();
    } catch (err) { setError(err.response?.data?.message || 'Error updating requirement'); }
  };

  const handleDelete = async () => {
    try {
      await requirementApi.remove(id);
      navigate('/requirements');
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting requirement');
      setDeleteConfirm(false);
    }
  };

  // ── Procurement handlers ───────────────────────────────────────────────────
  const handleProcurementSend = async () => {
    setError('');
    try {
      await procurementApi.create({
        requirementId: req.id,
        quantity:      parseInt(procForm.quantity),
        remarks:       procForm.remarks || null,
      });
      setProcSentConfirm(false);
      setProcurementForm(false);
      setProcForm({ quantity: '', remarks: '' });
      load();
    } catch (err) {
      setProcSentConfirm(false);
      setError(err.response?.data?.message || 'Error marking as sent');
    }
  };

  const handleProcurementReceive = async () => {
    setError('');
    try {
      await procurementApi.receive(receiveConfirm.procId);
      setReceiveConfirm(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error marking as received');
      setReceiveConfirm(null);
    }
  };

  const handleProcurementCancel = async () => {
    setError('');
    try {
      await procurementApi.cancel(cancelProcId);
      setCancelProcId(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error cancelling procurement');
      setCancelProcId(null);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!req) return <p className="text-gray-500 p-6">{error || 'Requirement not found'}</p>;

  const deficit              = req.quantityRequired - req.quantityFulfilled;
  const canEdit              = canWrite && req.status === 'open';
  const isActive             = ['open', 'partially_fulfilled'].includes(req.status);
  const isClosedOut          = ['fulfilled', 'received', 'cancelled'].includes(req.status);
  const hasActiveProcurement = req.procurements?.some((p) => p.status === 'sent');
  const canReceiveHere       = canReceive && (!isSiteRestricted || user?.siteId === req.siteId);
  const qtyReceived          = (req.procurements || []).reduce(
    (sum, p) => (p.status === 'received' ? sum + p.quantity : sum), 0
  );
  const inTransitCount       = req.procurements?.filter((p) => p.status === 'sent').length ?? 0;
  // While any transfer for this requirement is still pending (created but not
  // yet dispatched), block creating another one — regardless of source site —
  // to prevent duplicate transfers covering the same deficit.
  const pendingRequirementTransfer = req.transfers?.find((t) => t.status === 'pending');

  return (
    <div className="max-w-4xl">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/requirements')} className="text-gray-500 hover:text-gray-700 text-sm">← Back</button>
          <h1 className="text-2xl font-bold text-gray-900">Requirement #{req.id}</h1>
          <StatusBadge status={req.status} label={getReqStatusLabel(req)} />
          <span className="text-sm">{priorityLabel[req.priority]}</span>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <button
              className="btn-secondary text-sm"
              onClick={() => setEditForm({
                quantityRequired: req.quantityRequired,
                expectedDate:     req.expectedDate ? req.expectedDate.split('T')[0] : '',
                priority:         req.priority,
                remarks:          req.remarks || '',
              })}
            >
              Edit
            </button>
            <button
              className="text-red-600 border border-red-200 hover:bg-red-50 text-sm px-3 py-1.5 rounded-lg transition-colors"
              onClick={() => setDeleteConfirm(true)}
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {error && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</p>}

      {/* ── Status Banner ─────────────────────────────────────────────────── */}
      {['partially_fulfilled', 'fulfilled'].includes(req.status) && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4">
          <span className="text-amber-500 text-lg leading-none mt-0.5">⚠</span>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800">{getReqStatusLabel(req)}</p>
            <div className="text-sm text-amber-700 mt-0.5 space-y-0.5">
              {req.status === 'partially_fulfilled' ? (
                <>
                  <p>
                    <strong>{req.quantityFulfilled}</strong> of <strong>{req.quantityRequired}</strong> {req.kit?.unit} dispatched
                    {qtyReceived > 0 && <> · <strong>{qtyReceived} {req.kit?.unit} received</strong></>}
                    {inTransitCount > 0 && <> · <strong>{inTransitCount} batch(es) in transit</strong></>}
                    {' '}· <strong>{deficit} {req.kit?.unit} still to be dispatched.</strong>
                  </p>
                  {canSend && deficit > 0 && (
                    <p className="text-xs text-amber-600">Use "Mark as Sent" below to dispatch the remaining {deficit} {req.kit?.unit}.</p>
                  )}
                </>
              ) : (
                // fulfilled — all dispatched
                <>
                  <p>
                    All <strong>{req.quantityRequired} {req.kit?.unit}</strong> dispatched.{' '}
                    {qtyReceived > 0
                      ? <><strong>{qtyReceived} {req.kit?.unit} received</strong> · <strong>{req.quantityRequired - qtyReceived} {req.kit?.unit} in transit.</strong></>
                      : <strong>Awaiting receipt by the site.</strong>
                    }
                  </p>
                  {inTransitCount > 0 && (
                    <p className="text-xs text-sky-700">⏳ {inTransitCount} batch(es) in transit.</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Details + Suggestions ──────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* Details */}
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Details</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-gray-500">Site</dt><dd className="font-medium">{req.site?.name}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Group</dt><dd>{req.site?.group?.name}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Safety Kit</dt><dd className="font-medium">{req.kit?.name}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Required</dt><dd className="font-semibold text-lg text-blue-600">{req.quantityRequired}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Fulfilled</dt><dd className="font-medium text-green-600">{req.quantityFulfilled}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Remaining</dt><dd className={`font-semibold ${deficit > 0 ? 'text-red-600' : 'text-green-600'}`}>{deficit}</dd></div>
            {req.quantityRequired > 0 && (
              <div className="pt-1">
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${deficit <= 0 ? 'bg-green-500' : 'bg-amber-400'}`}
                    style={{ width: `${Math.min(100, (req.quantityFulfilled / req.quantityRequired) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1 text-right">
                  {Math.round((req.quantityFulfilled / req.quantityRequired) * 100)}% sent
                </p>
              </div>
            )}
            <div className="flex justify-between"><dt className="text-gray-500">Expected By</dt><dd>{req.expectedDate ? new Date(req.expectedDate).toLocaleDateString('en-GB') : '—'}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Priority</dt><dd>{priorityLabel[req.priority]}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Raised By</dt><dd>{req.createdByUsername || '—'}</dd></div>
            {req.remarks && <div className="flex justify-between"><dt className="text-gray-500">Remarks</dt><dd>{req.remarks}</dd></div>}
          </dl>
        </div>

        {/* Transfer Suggestions — only for users who hold the Transfer permission;
            hidden entirely (not just the action button) for a Storekeeper who
            can raise requirements but isn't authorized to create transfers. */}
        {hasPermission('transfer_request') && (
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Transfer Suggestions</h3>
            {!isClosedOut && (
              <button onClick={handleRefresh} disabled={refreshing} className="text-xs text-blue-600 hover:underline">
                {refreshing ? 'Refreshing...' : '↻ Refresh'}
              </button>
            )}
          </div>
          {isClosedOut ? (
            <div className="text-sm text-gray-400 py-4 text-center">
              <p>
                {req.status === 'received'
                  ? '✅ Fully Received — all materials confirmed at site. No transfers needed.'
                  : req.status === 'fulfilled'
                  ? qtyReceived > 0
                    ? `⏳ In Transit — ${qtyReceived} ${req.kit?.unit} received, ${req.quantityRequired - qtyReceived} still on the way. No further transfers needed.`
                    : '⏳ Fully Dispatched — awaiting receipt by the site. No further transfers needed.'
                  : '🚫 Requirement is cancelled. No transfers applicable.'}
              </p>
            </div>
          ) : req.suggestions?.length === 0 ? (
            <div className="text-sm text-gray-400 py-4 text-center">
              <p>No suggestions available yet.</p>
              <p className="text-xs mt-1">This happens if no other site in the group has stock.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {req.suggestions?.map((s) => {
                const pendingForThisSite = req.transfers?.find((t) => t.status === 'pending' && t.fromSiteId === s.sourceSiteId);
                return (
                <div key={s.id} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-sm">{s.sourceSite?.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Available: <strong>{s.availableQuantity}</strong> · Suggested: <strong>{s.suggestedQuantity}</strong>
                        {s.distanceKm != null && ` · ${s.distanceKm} km`}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">Score: {s.score}</span>
                      {isActive && hasPermission('transfer_request') && (
                        <div className="mt-2">
                          {pendingForThisSite ? (
                            <button
                              className="text-xs text-red-600 border border-red-200 hover:bg-red-50 py-1 px-2 rounded-lg transition-colors"
                              onClick={() => handleCancelTransfer(pendingForThisSite.id)}
                            >
                              Cancel
                            </button>
                          ) : pendingRequirementTransfer ? (
                            <button
                              className="text-xs btn-primary py-1 px-2 opacity-50 cursor-not-allowed"
                              disabled
                              title="Another transfer is already pending for this requirement — cancel it, or wait for it to be dispatched, before creating a new one"
                            >
                              Transfer
                            </button>
                          ) : (
                            <button
                              className="text-xs btn-primary py-1 px-2"
                              onClick={() => setTransferForm({ fromSiteId: s.sourceSiteId, quantity: s.suggestedQuantity, remarks: '' })}
                            >
                              Transfer
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
        )}
      </div>

      {/* ── External Procurement Section ───────────────────────────────────── */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">External Procurement</h3>
            <p className="text-xs text-gray-400 mt-0.5">Materials dispatched by the Procurement Department directly to this site</p>
          </div>
          {canSend && isActive && deficit > 0 && (
            <button
              className="btn-primary text-xs py-1.5 px-3"
              onClick={() => {
                setProcForm({ quantity: String(deficit), remarks: '' });
                setProcurementForm(true);
                setError('');
              }}
            >
              + Mark as Sent
            </button>
          )}
        </div>

        {(!req.procurements || req.procurements.length === 0) ? (
          <p className="text-sm text-gray-400 text-center py-4">No procurement records for this requirement.</p>
        ) : (
          <div className="space-y-2">
            {req.procurements.map((p) => (
              <div key={p.id} className={`flex items-center justify-between border rounded-lg px-4 py-3 ${
                p.status === 'received'  ? 'bg-emerald-50 border-emerald-100' :
                p.status === 'cancelled' ? 'bg-gray-50 border-gray-100 opacity-60' :
                'bg-sky-50 border-sky-100'
              }`}>
                <div className="flex items-center gap-3">
                  <StatusBadge status={p.status} />
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {p.quantity} {req.kit?.unit}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Dispatched by <strong>{p.sentByUsername || '—'}</strong> on {new Date(p.sentAt).toLocaleDateString('en-GB')}
                      {p.receivedAt && (
                        <> · Received on {new Date(p.receivedAt).toLocaleDateString('en-GB')} by <strong>{p.receivedByUsername || '—'}</strong></>
                      )}
                    </p>
                    {p.remarks && <p className="text-xs text-gray-400 italic mt-0.5">"{p.remarks}"</p>}
                  </div>
                </div>
                <div className="flex gap-2 ml-4 flex-shrink-0">
                  {p.status === 'sent' && canReceiveHere && (
                    <button
                      className="text-xs btn-primary py-1 px-3"
                      onClick={() => { setReceiveConfirm({ step: 1, procId: p.id, proc: p }); setError(''); }}
                    >
                      Mark as Received
                    </button>
                  )}
                  {p.status === 'sent' && canSend && (
                    <button
                      className="text-xs text-red-500 border border-red-200 hover:bg-red-50 px-3 py-1 rounded-lg transition-colors"
                      onClick={() => setCancelProcId(p.id)}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Edit Modal ──────────────────────────────────────────────────────── */}
      {editForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit Requirement</h3>
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Quantity Required</label>
                  <input className="input" type="number" min="1" value={editForm.quantityRequired} onChange={(e) => setEditForm({ ...editForm, quantityRequired: e.target.value })} required />
                </div>
                <div>
                  <label className="label">Priority</label>
                  <select className="input" value={editForm.priority} onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}>
                    <option value={1}>High</option>
                    <option value={2}>Medium</option>
                    <option value={3}>Low</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="label">Expected By</label>
                <input className="input" type="date" value={editForm.expectedDate} onChange={(e) => setEditForm({ ...editForm, expectedDate: e.target.value })} />
              </div>
              <div>
                <label className="label">Remarks</label>
                <textarea className="input" value={editForm.remarks} onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })} rows={2} />
              </div>
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <div className="flex gap-3 justify-end">
                <button type="button" className="btn-secondary" onClick={() => setEditForm(null)}>Cancel</button>
                <button type="submit" className="btn-primary">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Transfer Modal ──────────────────────────────────────────────────── */}
      {transferForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Transfer</h3>
            <form onSubmit={handleTransfer} className="space-y-4">
              <div>
                <label className="label">Quantity</label>
                <input
                  className="input"
                  type="number"
                  min="1"
                  value={transferForm.quantity}
                  onChange={(e) => { setTransferForm({ ...transferForm, quantity: e.target.value }); setTransferUnitIds([]); }}
                  required
                />
              </div>
              {req.kit?.requiresMaterialId && (
                <div>
                  <label className="label">Select {transferForm.quantity || 0} Material ID(s)</label>
                  <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto divide-y divide-gray-100">
                    {transferAvailableUnits.length === 0 && (
                      <p className="text-sm text-gray-400 p-3">No in-stock units available to select at the source site.</p>
                    )}
                    {transferAvailableUnits.map((u) => (
                      <label key={u.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
                        <input type="checkbox" checked={transferUnitIds.includes(u.id)} onChange={() => toggleTransferUnit(u.id)} />
                        <span className="font-mono">{u.materialIdCode}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{transferUnitIds.length} of {transferForm.quantity || 0} selected</p>
                </div>
              )}
              <div>
                <label className="label">Remarks</label>
                <textarea className="input" value={transferForm.remarks} onChange={(e) => setTransferForm({ ...transferForm, remarks: e.target.value })} rows={2} />
              </div>
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <div className="flex gap-3 justify-end">
                <button type="button" className="btn-secondary" onClick={() => setTransferForm(null)}>Cancel</button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={req.kit?.requiresMaterialId && transferUnitIds.length !== parseInt(transferForm.quantity || 0)}
                >
                  Create Transfer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Mark as Sent — Form Modal ───────────────────────────────────────── */}
      {procurementForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-1">Mark Material as Sent</h3>
            <p className="text-sm text-gray-500 mb-4">
              Record that the Procurement Department has dispatched <strong>{req.kit?.name}</strong> to <strong>{req.site?.name}</strong>.
              Inventory will only be updated after the storekeeper confirms receipt.
            </p>
            <form onSubmit={(e) => { e.preventDefault(); setError(''); setProcSentConfirm(true); }} className="space-y-4">
              <div>
                <label className="label">
                  Quantity <span className="text-red-500">*</span>
                  {deficit > 0 && (
                    <span className="ml-2 text-xs font-normal text-amber-600">
                      ({deficit} {req.kit?.unit} still needed)
                    </span>
                  )}
                </label>
                <input
                  className="input"
                  type="number"
                  min="1"
                  max={deficit > 0 ? deficit : undefined}
                  value={procForm.quantity}
                  onChange={(e) => setProcForm({ ...procForm, quantity: e.target.value })}
                  required
                />
                {deficit > 0 && parseInt(procForm.quantity) > deficit && (
                  <p className="text-amber-600 text-xs mt-1">
                    ⚠ Quantity exceeds the remaining deficit of {deficit}. This will over-fulfil the requirement.
                  </p>
                )}
              </div>
              <div>
                <label className="label">Remarks</label>
                <textarea
                  className="input"
                  rows={2}
                  value={procForm.remarks}
                  onChange={(e) => setProcForm({ ...procForm, remarks: e.target.value })}
                />
              </div>
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <div className="flex gap-3 justify-end">
                <button type="button" className="btn-secondary" onClick={() => { setProcurementForm(false); setProcForm({ quantity: '', expiryDate: '', remarks: '' }); }}>Cancel</button>
                <button type="submit" className="btn-primary">Mark as Sent</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Confirm: Mark as Sent ───────────────────────────────────────────── */}
      <ConfirmDialog
        open={procSentConfirm}
        danger={false}
        title="Confirm Dispatch"
        message={`Dispatch ${procForm.quantity} ${req.kit?.unit} of ${req.kit?.name} to ${req.site?.name}?`}
        warning="This will create a procurement record and notify the site storekeeper. Inventory will be updated only after the storekeeper confirms receipt. This record cannot be edited once submitted."
        confirmLabel="Confirm & Submit"
        cancelLabel="Go Back"
        onConfirm={handleProcurementSend}
        onCancel={() => setProcSentConfirm(false)}
      />

      {/* ── Confirm Receive — Step 1 ─────────────────────────────────────────── */}
      <ConfirmDialog
        open={receiveConfirm?.step === 1}
        danger={false}
        title="Mark as Received?"
        message={
          receiveConfirm
            ? `You are about to confirm receipt of ${receiveConfirm.proc.quantity} ${req.kit?.unit} of ${req.kit?.name} at ${req.site?.name}.`
            : ''
        }
        warning="Please ensure the material has been physically received and verified before proceeding. This will update inventory and transaction records."
        confirmLabel="Yes, Proceed"
        cancelLabel="Cancel"
        onConfirm={() => setReceiveConfirm((prev) => ({ ...prev, step: 2 }))}
        onCancel={() => setReceiveConfirm(null)}
      />

      {/* ── Confirm Receive — Step 2 (final, irreversible) ──────────────────── */}
      <ConfirmDialog
        open={receiveConfirm?.step === 2}
        danger={true}
        title="Final Confirmation"
        message={
          receiveConfirm
            ? `Confirm receipt of ${receiveConfirm.proc.quantity} ${req.kit?.unit}. This quantity will be added to the site inventory immediately.`
            : ''
        }
        warning="This action will permanently update inventory records and cannot be reversed. Proceed only if the material has been physically received and verified."
        confirmLabel="Confirm Receipt"
        cancelLabel="Go Back"
        onConfirm={handleProcurementReceive}
        onCancel={() => setReceiveConfirm((prev) => ({ ...prev, step: 1 }))}
      />

      {/* ── Confirm: Delete Requirement ─────────────────────────────────────── */}
      <ConfirmDialog
        open={deleteConfirm}
        title="Delete Requirement"
        message={`Permanently delete Requirement #${req.id} for ${req.kit?.name} at ${req.site?.name}?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm(false)}
      />

      {/* ── Confirm: Cancel Procurement ─────────────────────────────────────── */}
      <ConfirmDialog
        open={!!cancelProcId}
        title="Cancel Procurement"
        message="Cancel this procurement dispatch? The storekeeper will no longer be expected to receive this material."
        onConfirm={handleProcurementCancel}
        onCancel={() => setCancelProcId(null)}
      />
    </div>
  );
}
